/**
 * Analytics Hub — single home for every insights view.
 *
 * The old Doctor Analytics page (`/app/analytics/doctors`) has been folded
 * into this tabbed hub. Existing deep-links keep working — they land on the
 * Doctors tab.
 *
 * Phase A ships the tab shell + the two tabs we already have data for
 * (Doctors, Financial Reports). The remaining tabs render a lightweight
 * "coming soon" strip so the nav feels complete without shipping empty
 * charts.
 */
import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { BarChart3, DollarSign, Users, Stethoscope, CalendarDays, Package, MessageCircle, FileBarChart } from "lucide-react";
import ViyoraSkeleton from "@/components/ViyoraSkeleton";

const TABS = [
  { to: "overview",    label: "Overview",           icon: BarChart3 },
  { to: "revenue",     label: "Revenue",            icon: DollarSign },
  { to: "patients",    label: "Patients",           icon: Users },
  { to: "doctors",     label: "Doctors",            icon: Stethoscope },
  { to: "appointments",label: "Appointments",       icon: CalendarDays },
  { to: "inventory",   label: "Inventory",          icon: Package },
  { to: "whatsapp",    label: "WhatsApp",           icon: MessageCircle },
  { to: "reports",     label: "Financial Reports",  icon: FileBarChart },
];

export default function AnalyticsHub() {
  const location = useLocation();
  // When the user lands on `/app/analytics` with no sub-tab, forward to
  // Overview via a client-side redirect (no history entry).
  useEffect(() => {
    if (location.pathname === "/app/analytics") {
      window.history.replaceState(null, "", "/app/analytics/overview");
    }
  }, [location.pathname]);

  return (
    <div className="space-y-6" data-testid="analytics-hub">
      <div>
        <div className="overline text-ink-soft">Insights</div>
        <h1 className="mt-2 font-display text-4xl tracking-tight">Analytics</h1>
        <p className="mt-2 text-sm text-ink-mid max-w-2xl">
          Every KPI your clinic runs on — revenue, patients, doctors, appointments,
          inventory, WhatsApp — in one workspace.
        </p>
      </div>

      {/* Tab strip — sticky under the header so long dashboards keep it in view. */}
      <div className="flex flex-wrap gap-1 border-b border-[#E8EAE9] pb-0" data-testid="analytics-tabs">
        {TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            data-testid={`analytics-tab-${t.to}`}
            className={({ isActive }) =>
              `inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${
                isActive ? "border-pine text-pine" : "border-transparent text-ink-soft hover:text-ink-mid"
              }`
            }
          >
            <t.icon className="w-3.5 h-3.5" />
            {t.label}
          </NavLink>
        ))}
      </div>

      <Outlet />
    </div>
  );
}

/** Lightweight "we're planning this" strip for tabs that don't yet have a
 *  full-fat dashboard. Keeps the nav feeling complete without shipping empty
 *  chart shells. */
export function AnalyticsComingSoon({ title, description }) {
  return (
    <div className="rounded-2xl border border-dashed border-[#E8EAE9] bg-white p-10 text-center max-w-2xl mx-auto" data-testid="analytics-coming-soon">
      <BarChart3 className="w-8 h-8 mx-auto text-ink-soft" />
      <h2 className="mt-4 font-display text-2xl">{title}</h2>
      <p className="mt-2 text-sm text-ink-mid">{description}</p>
      <div className="mt-4 text-[10px] uppercase tracking-widest text-ink-soft">Coming soon</div>
    </div>
  );
}

export function AnalyticsOverview() {
  const [dash, setDash] = useState(null);
  useEffect(() => {
    import("@/lib/api").then(({ api }) =>
      api.get("/dashboard/summary").then((r) => setDash(r.data)).catch(() => {})
    );
  }, []);
  if (!dash) return <ViyoraSkeleton variant="stat" count={8} />;
  const fmtRs = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;
  return (
    <div className="space-y-5" data-testid="analytics-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Revenue · 30d"       value={fmtRs(dash.revenue_30d)} />
        <StatCard label="Outstanding"         value={fmtRs(dash.outstanding)} warn={(dash.outstanding || 0) > 0} />
        <StatCard label="Collections rate"    value={`${dash.collections_rate ?? 0}%`} />
        <StatCard label="Revenue · today"     value={fmtRs(dash.revenue_today)} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Patients (total)"    value={dash.patients_total ?? 0} />
        <StatCard label="New patients · 30d"  value={dash.new_patients_30d ?? 0} />
        <StatCard label="Appointments · 30d"  value={dash.appts_30d ?? 0} />
        <StatCard label="No-show rate · 30d"  value={`${dash.no_show_rate_30d ?? 0}%`} warn={(dash.no_show_rate_30d || 0) > 10} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Inventory value"     value={fmtRs(dash.inventory_value)} />
        <StatCard label="Low-stock items"     value={dash.low_stock_count ?? 0} warn={(dash.low_stock_count || 0) > 0} />
        <StatCard label="Open alerts"         value={dash.alerts_count ?? 0} />
        <StatCard label="Appointments today"  value={dash.appts_today ?? 0} />
      </div>
      <div className="text-[11px] text-ink-soft">
        Every value comes from the same <code>analytics_service</code> query used by the dashboard and reports.
        View source queries at <a href="/app/settings/workspace/kpi-audit" className="text-pine underline">Settings → Workspace → KPI Audit</a> (admin only).
      </div>
    </div>
  );
}

function StatCard({ label, value, warn }) {
  return (
    <div className={`rounded-2xl border p-5 ${warn ? "bg-[#FCECEC] border-[#EDC7C6]" : "bg-white border-[#E8EAE9]"}`}>
      <div className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</div>
      <div className={`mt-1 font-display text-3xl ${warn ? "text-[#B94A48]" : ""}`}>{value}</div>
    </div>
  );
}
